import { PencilIcon, PlusIcon } from "@heroicons/react/outline";
import { useEffect, useState } from "react";
import DeleteModal from "../../../../components/DeleteModal/DeleteModal";
import { apiV3 } from "../../../../utils/helpers/api";
import { useToast } from "../../../../components/Toastr/Toastr";

// Operational needs offered by this AEV location's PreCheck kiosks — the
// AEV-location counterpart of a Self Serve location's operational
// requirements. Kiosks receive the ACTIVE names (in order) through their
// device lookup, add their own "Other" row, and store the chosen name as free
// text on the check-in. Each location was seeded with the former global list.
export default function OperationalNeedsSection({ locationId }: { locationId: string }) {
  const { showSuccessToast, showErrorToast } = useToast();
  const [loading, setLoading] = useState<boolean>(true);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [operationalNeeds, setOperationalNeeds] = useState<any[]>([]);
  const [newName, setNewName] = useState<string>("");
  const [newSortOrder, setNewSortOrder] = useState<string>("1");
  const [editingId, setEditingId] = useState<string>("");
  const [editName, setEditName] = useState<string>("");
  const [editSortOrder, setEditSortOrder] = useState<string>("");
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [operationalNeedIdToDelete, setOperationalNeedIdToDelete] = useState<string>("");

  useEffect(() => {
    fetchOperationalNeeds();
  }, []);

  const nextSortOrder = (needs: any[]) =>
    `${needs.reduce((max, need) => Math.max(max, need.sortOrder ?? 0), 0) + 1}`;

  const fetchOperationalNeeds = async () => {
    try {
      const res = await apiV3.getLocationOperationalNeeds(locationId);
      if (res.status === 200) {
        setOperationalNeeds(res.data);
        setNewSortOrder(nextSortOrder(res.data));
      }
    } catch (error) {
      console.error("Error occurred while fetching operational needs - ", error);
      showErrorToast();
    } finally {
      setLoading(false);
    }
  };

  const conflictToast = (error: any, fallback: () => void) => {
    if (error?.response?.status === 409) {
      showErrorToast("An operational need with that name already exists for this location");
    } else {
      fallback();
    }
  };

  const addOperationalNeed = async () => {
    setIsBusy(true);
    try {
      await apiV3.createLocationOperationalNeed(locationId, {
        name: newName.trim(),
        sortOrder: parseInt(newSortOrder, 10) || 0,
      });
      showSuccessToast("Successfully added operational need");
      setNewName("");
      await fetchOperationalNeeds();
    } catch (error: any) {
      console.error("Error occurred while adding operational need - ", error);
      conflictToast(error, showErrorToast);
    } finally {
      setIsBusy(false);
    }
  };

  const startEdit = (need: any) => {
    setEditingId(need.id);
    setEditName(need.name);
    setEditSortOrder(`${need.sortOrder ?? 0}`);
  };

  const cancelEdit = () => {
    setEditingId("");
    setEditName("");
    setEditSortOrder("");
  };

  const saveEdit = async () => {
    setIsBusy(true);
    try {
      await apiV3.updateLocationOperationalNeedById(locationId, editingId, {
        name: editName.trim(),
        sortOrder: parseInt(editSortOrder, 10) || 0,
      });
      showSuccessToast("Successfully updated operational need");
      cancelEdit();
      await fetchOperationalNeeds();
    } catch (error: any) {
      console.error("Error occurred while updating operational need - ", error);
      conflictToast(error, showErrorToast);
    } finally {
      setIsBusy(false);
    }
  };

  const toggleOperationalNeed = async (need: any) => {
    setIsBusy(true);
    try {
      await apiV3.updateLocationOperationalNeedById(locationId, need.id, {
        active: !need.active,
      });
      showSuccessToast(`Successfully ${need.active ? "deactivated" : "activated"} operational need`);
      await fetchOperationalNeeds();
    } catch (error) {
      console.error("Error occurred while updating operational need - ", error);
      showErrorToast();
    } finally {
      setIsBusy(false);
    }
  };

  const deleteOperationalNeed = async () => {
    setIsBusy(true);
    setOpenDeleteModal(false);
    try {
      await apiV3.deleteLocationOperationalNeedById(locationId, operationalNeedIdToDelete);
      showSuccessToast("Successfully deleted operational need");
      setOperationalNeedIdToDelete("");
      await fetchOperationalNeeds();
    } catch (error) {
      console.error("Error occurred while deleting operational need - ", error);
      showErrorToast();
    } finally {
      setIsBusy(false);
    }
  };

  const inputClass =
    "block w-full rounded-md border-gray-300 shadow-sm focus:ring-amber-500 focus:border-amber-500 sm:text-sm";

  return (
    <div className="mt-6">
      <h4 className="text-md leading-6 font-medium text-gray-900">Operational Needs</h4>
      <p className="mt-1 text-sm text-gray-500">
        The reasons for airside access offered on this location's PreCheck kiosks, in the order
        shown here. Kiosks always add an "Other" option with free text, so there is no need to
        list it. Deactivate a need to hide it without losing it; changes reach the kiosks within
        a minute.
      </p>
      {loading ? (
        <div className="mt-2 text-sm text-gray-500">Loading...</div>
      ) : operationalNeeds.length === 0 ? (
        <div className="mt-2 text-sm text-gray-500">
          No operational needs for this location — kiosks here will only offer "Other".
        </div>
      ) : (
        <div className="my-3">
          <table className="min-w-full divide-y divide-gray-300">
            <thead>
              <tr className="text-left text-sm font-semibold text-gray-900">
                <th className="py-2 pr-3">Order</th>
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {operationalNeeds.map((need: any) =>
                editingId === need.id ? (
                  <tr key={need.id} className="text-sm">
                    <td className="py-2 pr-3 w-24">
                      <input
                        type="number"
                        value={editSortOrder}
                        disabled={isBusy}
                        onChange={(e) => setEditSortOrder(e.target.value)}
                        className={inputClass}
                      />
                    </td>
                    <td className="py-2 pr-3" colSpan={2}>
                      <input
                        type="text"
                        value={editName}
                        disabled={isBusy}
                        autoFocus
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && editName.trim()) saveEdit();
                          if (e.key === "Escape") cancelEdit();
                        }}
                        className={inputClass}
                      />
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <a
                        onClick={() => !isBusy && editName.trim() && saveEdit()}
                        className="text-green-600 hover:text-green-800 cursor-pointer"
                      >
                        Save
                      </a>
                      <a
                        onClick={() => !isBusy && cancelEdit()}
                        className="ml-4 text-gray-600 hover:text-gray-900 cursor-pointer"
                      >
                        Cancel
                      </a>
                    </td>
                  </tr>
                ) : (
                  <tr key={need.id} className="text-sm">
                    <td className="py-3 pr-3 text-gray-500">{need.sortOrder}</td>
                    <td className="py-3 pr-3 font-medium text-gray-900">{need.name}</td>
                    <td className="py-3 pr-3">
                      {need.active ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="py-3 text-right whitespace-nowrap">
                      <a
                        onClick={() => !isBusy && startEdit(need)}
                        className="inline-flex items-center text-indigo-600 hover:text-indigo-900 cursor-pointer"
                      >
                        <PencilIcon className="h-4 w-4 mr-1" />
                        Edit
                      </a>
                      <a
                        onClick={() => !isBusy && toggleOperationalNeed(need)}
                        className="ml-4 text-amber-600 hover:text-amber-800 cursor-pointer"
                      >
                        {need.active ? "Deactivate" : "Activate"}
                      </a>
                      <a
                        onClick={() => {
                          setOpenDeleteModal(true);
                          setOperationalNeedIdToDelete(need.id);
                        }}
                        className="ml-4 text-red-600 hover:text-red-900 cursor-pointer"
                      >
                        Delete
                      </a>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-4 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6">
        <div className="sm:col-span-3">
          <label htmlFor="operational-need-name" className="block text-sm font-medium text-gray-700">
            Name
          </label>
          <input
            type="text"
            id="operational-need-name"
            value={newName}
            disabled={isBusy}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newName.trim() && !isBusy) addOperationalNeed();
            }}
            placeholder="e.g. Boarding Duties"
            className={`mt-1 ${inputClass}`}
          />
        </div>
        <div className="sm:col-span-1">
          <label htmlFor="operational-need-order" className="block text-sm font-medium text-gray-700">
            Order
          </label>
          <input
            type="number"
            id="operational-need-order"
            value={newSortOrder}
            disabled={isBusy}
            onChange={(e) => setNewSortOrder(e.target.value)}
            className={`mt-1 ${inputClass}`}
          />
        </div>
        <div className="sm:col-span-2 flex items-end">
          <button
            type="button"
            onClick={() => addOperationalNeed()}
            disabled={isBusy || !newName.trim()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Operational Need
          </button>
        </div>
      </div>
      <DeleteModal
        description="Deleting an operational need removes the option from this location's kiosks within a minute. Existing check-ins are unaffected."
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        title={"Are you sure you want to delete this operational need?"}
        action={() => deleteOperationalNeed()}
        actionButtonText={"Delete"}
      />
    </div>
  );
}
